import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Table } from 'react-bootstrap';
import { NumericFormat, NumericFormat as NumberFormat } from 'react-number-format';
import { CustomButton, Loading } from '../../_common';
import { GetBoxes_getBoxes_boxDetails as BoxDetails } from '../../../types/schemaTypes';
import './Inventory.scss';

export interface SpillRowData {
    key: string;
    boxId: number;
    boxDesc: string;
    loadId: number | null;
    ticketWeight: number;
    actualWeight: number;
    sandTicketNumber: string;
    spillWeight: number | '';
}

interface SpillModalProps {
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    selectedBoxes: BoxDetails[];
    mutationLoading: boolean;
    modalError: string;
    setModalError: React.Dispatch<React.SetStateAction<string>>;
    onSubmit: (rows: SpillRowData[]) => void;
}

export const buildSpillRowsFromBoxes = (boxes: BoxDetails[]): SpillRowData[] => {
    const rows: SpillRowData[] = [];

    boxes.forEach(box => {
        if (!box.boxId) {
            return;
        }

        const bellyLoads = box.stageSheetBellyDump?.filter(load => load != null) ?? [];

        if (bellyLoads.length > 0) {
            bellyLoads.forEach((load, index) => {
                const ticketWeight = load?.allocatedWt ?? 0;
                const actualWeight = load?.remainingWt ?? 0;

                rows.push({
                    key: `${box.boxId}-${load?.loadId ?? index}`,
                    boxId: box.boxId as number,
                    boxDesc: box.boxDesc ?? '',
                    loadId: load?.loadId ?? null,
                    ticketWeight,
                    actualWeight,
                    sandTicketNumber: load?.sandTicket ?? '',
                    spillWeight: 0,
                });
            });
            return;
        }

        const ticketWeight = box.loadWeight ?? 0;
        const actualWeight = box.currentProppantWeight ?? 0;

        rows.push({
            key: `${box.boxId}-${box.loadId ?? 'default'}`,
            boxId: box.boxId,
            boxDesc: box.boxDesc ?? '',
            loadId: box.loadId ?? null,
            ticketWeight,
            actualWeight,
            sandTicketNumber: box.sandTicket ?? '',
            spillWeight: 0,
        });
    });

    return rows;
};

export const isSpillWeightOverActual = (row: SpillRowData): boolean =>
    row.spillWeight !== '' &&
    !Number.isNaN(Number(row.spillWeight)) &&
    Number(row.spillWeight) > row.actualWeight;

const getSpillWeightExceedsActualError = (rows: SpillRowData[]): string => {
    const invalidRow = rows.find(isSpillWeightOverActual);

    if (!invalidRow) {
        return '';
    }

    const spill = Number(invalidRow.spillWeight);
    const label =
        invalidRow.loadId != null
            ? `${invalidRow.boxDesc} (Load #${invalidRow.loadId})`
            : invalidRow.boxDesc;

    return `Spill weight (${spill.toLocaleString()} lbs) cannot exceed actual weight (${invalidRow.actualWeight.toLocaleString()} lbs) for ${label}.`;
};

const SpillModal = ({
    showModal,
    setShowModal,
    selectedBoxes,
    mutationLoading,
    modalError,
    setModalError,
    onSubmit,
}: SpillModalProps): React.ReactElement => {
    const [spillRows, setSpillRows] = useState<SpillRowData[]>([]);

    // Initialize rows only when the modal opens. Do not depend on selectedBoxes —
    // Inventory passes a new array reference each render, which would reset user input.
    useEffect(() => {
        if (showModal) {
            setSpillRows(buildSpillRowsFromBoxes(selectedBoxes));
        }
    }, [showModal]);

    const spillValidationError = useMemo(
        () => (showModal ? getSpillWeightExceedsActualError(spillRows) : ''),
        [spillRows, showModal],
    );

    const onClickClose = () => {
        setShowModal(false);
        setModalError('');
    };

    const handleSpillWeightChange = (key: string, value: number | '') => {
        setSpillRows(prev =>
            prev.map(row => (row.key === key ? { ...row, spillWeight: value } : row)),
        );
    };

    const hasInvalidSpillWeight = spillRows.some(
        row =>
            row.spillWeight === '' ||
            Number.isNaN(Number(row.spillWeight)) ||
            Number(row.spillWeight) < 0 ||
            isSpillWeightOverActual(row),
    );

    const isSaveDisabled =
        spillRows.length === 0 || hasInvalidSpillWeight || mutationLoading;

    const handleSave = () => {
        if (spillValidationError || hasInvalidSpillWeight) {
            return;
        }

        setModalError('');
        onSubmit(spillRows);
    };

    return (
        <Modal show={showModal} onHide={onClickClose} size="lg">
            <Modal.Header className="Inventory-Setup-Header">
                <Modal.Title className="Inventory-Setup-Header-Container">
                    <button className="material-icons Inventory-Setup-X-Button" onClick={onClickClose}>
                        close
                    </button>
                    <div className="Inventory-Setup-Title">Record Spill</div>
                    <div className="Inventory-Setup-Error">{spillValidationError || modalError}</div>
                </Modal.Title>
            </Modal.Header>

            {mutationLoading && (
                <div className="Inventory-Setup-Spinner">
                    <Loading />
                </div>
            )}

            <Modal.Body className="Inventory-Setup-Body SpillModal-Body">
                <p className="SpillModal-HelperText">
                </p>
                <Table bordered hover className="SpillModal-Table">
                    <colgroup>
                        <col span={6} className="SpillModal-Col" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Box</th>
                            <th className="SpillModal-NumHeader">Ticket Weight</th>
                            <th className="SpillModal-NumHeader">Actual Weight</th>
                            <th>Sand Ticket #</th>
                            <th className="SpillModal-NumHeader">Load #</th>
                            <th className="SpillModal-NumHeader">Spill Weight</th>
                        </tr>
                    </thead>
                    <tbody>
                        {spillRows.map(row => (
                            <tr key={row.key}>
                                <td className="SpillModal-BoxCell">{row.boxDesc}</td>
                                <td className="SpillModal-NumCell">
                                    <NumberFormat
                                        value={row.ticketWeight}
                                        displayType="text"
                                        thousandSeparator
                                    />
                                </td>
                                <td className="SpillModal-NumCell">
                                    <NumberFormat
                                        value={row.actualWeight}
                                        displayType="text"
                                        thousandSeparator
                                    />
                                </td>
                                <td className="SpillModal-TicketCell">{row.sandTicketNumber || '—'}</td>
                                <td className="SpillModal-NumCell">{row.loadId ?? '—'}</td>
                                <td className="SpillModal-NumCell SpillModal-WeightCell">
                                    <NumericFormat
                                        className="EditBox-Input SpillModal-WeightInput"
                                        value={row.spillWeight}
                                        thousandSeparator
                                        allowNegative={false}
                                        onValueChange={values => {
                                            handleSpillWeightChange(
                                                row.key,
                                                values.floatValue === undefined ? '' : values.floatValue,
                                            );
                                        }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Modal.Body>

            <Modal.Footer className="Inventory-Setup-Footer">
                <CustomButton title="Cancel" isBordered onClick={onClickClose} />
                <CustomButton title="Save" onClick={handleSave} isDisabled={isSaveDisabled} />
            </Modal.Footer>
        </Modal>
    );
};

export default SpillModal;
